import React, { useEffect, useState } from 'react'
import { AuthService } from '@lura/shared'
import { AuthContext } from './AuthContextType'
import type { AuthUser } from '@lura/shared'

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const getUser = async () => {
      try {
        const currentUser = await AuthService.getCurrentUser()

        // If user exists but can't access web app, sign them out
        if (
          currentUser?.profile &&
          !AuthService.canAccessWebApp(currentUser.profile)
        ) {
          await AuthService.signOut()
          setUser(null)
        } else {
          setUser(currentUser)
        }
      } catch (error) {
        console.error('Error getting current user:', error)
      } finally {
        setLoading(false)
      }
    }

    getUser()
  }, [])

  const signIn = async (email: string, password: string) => {
    const { user: authUser } = await AuthService.signIn(email, password)

    if (authUser) {
      const currentUser = await AuthService.getCurrentUser()

      // Check if user can access web app
      if (
        currentUser?.profile &&
        !AuthService.canAccessWebApp(currentUser.profile)
      ) {
        // Sign out the user since they can't access this platform
        await AuthService.signOut()
        throw new Error(
          'This website is for healthcare providers only. Please use the mobile app to access your patient portal.'
        )
      }

      setUser(currentUser)
    }
  }

  const signUp = async (data: {
    email: string
    password: string
    first_name?: string
    last_name?: string
  }) => {
    await AuthService.signUp(data, 'web')

    // User will need to verify email before signing in
  }

  const signOut = async () => {
    await AuthService.signOut()
    setUser(null)
  }

  const resendConfirmation = async (email: string) => {
    await AuthService.resendConfirmation(email)
  }

  const resetPassword = async (email: string) => {
    await AuthService.resetPassword(
      email,
      'web',
      `${window.location.origin}/reset-password`
    )
  }

  const refreshUser = async () => {
    try {
      const currentUser = await AuthService.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Error refreshing user:', error)
    }
  }

  const canAccessApp = user?.profile
    ? AuthService.canAccessWebApp(user.profile)
    : false

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    resendConfirmation,
    resetPassword,
    refreshUser,
    canAccessApp
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
